// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
/**
 * AddressFields - V40
 * Herbruikbaar adresblok met Nederlandse postcode-autofill via BAG API.
 * Trigger: zodra postcode 6 tekens heeft EN huisnummer is ingevuld.
 * Handmatige invoer is altijd mogelijk - autofill blokkeert nooit.
 */
import { useCallback, useEffect, useRef } from 'react'
import { Loader2, CheckCircle2, AlertCircle } from 'lucide-react'
import { Input } from '@/components/ui/Input'
import { useAddressLookup } from '@/hooks/useAddressLookup'

export interface AddressFieldValues {
  postalCode:           string
  houseNumber:          string
  houseNumberAddition?: string
  street:               string
  city:                 string
}

interface AddressFieldsProps {
  values:         AddressFieldValues
  onChange:       (field: keyof AddressFieldValues, value: string) => void
  showAddition?:  boolean
  nlOnly?:        boolean
  labels?: {
    postalCode?:  string
    houseNumber?: string
    addition?:    string
    street?:      string
    city?:        string
  }
  disabled?: boolean
}

export function AddressFields({
  values,
  onChange,
  showAddition = true,
  nlOnly: _nlOnly = true,
  labels = {},
  disabled = false,
}: AddressFieldsProps) {
  const handleFound = useCallback((street: string, city: string) => {
    onChange('street', street)
    onChange('city', city)
  }, [onChange])

  const { status, lookup } = useAddressLookup({ onFound: handleFound })

  // Debounce timer - voorkom spammen van de API bij elke toetsaanslag
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Trigger lookup zodra postcode compleet (6 tekens) EN huisnummer ingevuld
  useEffect(() => {
    if (disabled) return
    const pc = values.postalCode.replace(/\s/g, '')
    const nr = values.houseNumber.trim()
    if (pc.length < 6 || !nr) return

    if (debounce.current) clearTimeout(debounce.current)
    debounce.current = setTimeout(() => {
      lookup(pc, nr)
    }, 600) // 600ms na laatste wijziging

    return () => {
      if (debounce.current) clearTimeout(debounce.current)
    }
  }, [values.postalCode, values.houseNumber, disabled, lookup])

  return (
    <div className="flex flex-col gap-3">
      {/* Rij 1: postcode + huisnummer + toevoeging */}
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="sm:col-span-1">
          <Input
            label={labels.postalCode ?? 'Postcode'}
            value={values.postalCode}
            onChange={e => onChange('postalCode', e.target.value)}
            placeholder="1234 AB"
            disabled={disabled}
          />
        </div>
        <div className="sm:col-span-1">
          <Input
            label={labels.houseNumber ?? 'Huisnummer'}
            value={values.houseNumber}
            onChange={e => onChange('houseNumber', e.target.value)}
            placeholder="12"
            disabled={disabled}
          />
        </div>
        {showAddition && (
          <div className="sm:col-span-1">
            <Input
              label={labels.addition ?? 'Toevoeging'}
              value={values.houseNumberAddition ?? ''}
              onChange={e => onChange('houseNumberAddition', e.target.value)}
              placeholder="A, bis…"
              hint="Optioneel"
              disabled={disabled}
            />
          </div>
        )}
      </div>

      {/* Statusregel */}
      {!disabled && status !== 'idle' && (
        <div className={`flex items-center gap-2 text-xs rounded-lg px-3 py-2 ${
          status === 'loading'   ? 'bg-sand text-ink-3' :
          status === 'found'     ? 'bg-sage-50 border border-sage-200 text-sage-700' :
                                   'bg-amber-50 border border-amber-200 text-amber-700'
        }`}>
          {status === 'loading'   && <Loader2      className="h-3.5 w-3.5 animate-spin flex-shrink-0" />}
          {status === 'found'     && <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" />}
          {status === 'not_found' && <AlertCircle  className="h-3.5 w-3.5 flex-shrink-0" />}
          <span>
            {status === 'loading'   && 'Adres zoeken\u2026'}
            {status === 'found'     && 'Adres gevonden'}
            {status === 'not_found' && 'Adres niet gevonden \u2014 vul straatnaam en woonplaats handmatig in'}
          </span>
        </div>
      )}

      {/* Rij 2: straatnaam + woonplaats */}
      <div className="grid gap-3 sm:grid-cols-2">
        <Input
          label={labels.street ?? 'Straatnaam'}
          value={values.street}
          onChange={e => onChange('street', e.target.value)}
          placeholder="Kerkstraat"
          disabled={disabled}
        />
        <Input
          label={labels.city ?? 'Woonplaats'}
          value={values.city}
          onChange={e => onChange('city', e.target.value)}
          placeholder="Amsterdam"
          disabled={disabled}
        />
      </div>
    </div>
  )
}
